import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import axios from 'axios';
import { API_URL } from '../../../api/api';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';

const CashLoanPaymentLayer = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [loan, setLoan] = useState(null);
    const [payments, setPayments] = useState([]);
    const [emiAmount, setEmiAmount] = useState('');
    const [transactionDate, setTransactionDate] = useState('');
    const [remark, setRemark] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchLoanDetails();
    }, [id]);

    const fetchLoanDetails = async () => {
        try {
            const token = localStorage.getItem('token');
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            const response = await axios.get(`${API_URL}/cash-loans/${id}`);
            setLoan(response.data);
            setPayments(response.data.payments || []);
        } catch (error) {
            console.error('Error fetching loan details:', error);
            Swal.fire('Error', 'Failed to load loan details', 'error');
        } finally {
            setLoading(false);
        }
    };

    const calculatedEmi = loan
        ? (loan.loan_amount_with_interest / loan.tenure).toFixed(2)
        : '0.00';

    const handlePayEmi = async (e) => {
        e.preventDefault();

        if (!emiAmount || isNaN(emiAmount)) {
            return Swal.fire('Invalid', 'Please enter a valid EMI amount', 'warning');
        }

        if (!transactionDate) {
            return Swal.fire('Invalid', 'Please select a transaction date', 'warning');
        }

        try {
            const token = localStorage.getItem('token');
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

            await axios.post(`${API_URL}/cash-loans/${id}/payments`, {
                amount: emiAmount,
                payment_date: transactionDate,
                notes: remark.trim(),
            });

            Swal.fire('Success', 'EMI payment recorded', 'success');
            setEmiAmount('');
            setTransactionDate('');
            setRemark('');
            fetchLoanDetails();
        } catch (error) {
            console.error('Error submitting payment:', error);
            Swal.fire('Error', 'Failed to record EMI payment', 'error');
        }
    };

    const handleDeletePayment = async (paymentId) => {
        const confirm = await Swal.fire({
            title: 'Are you sure?',
            text: 'Do you want to delete this payment?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
        });

        if (!confirm.isConfirmed) return;

        try {
            const token = localStorage.getItem('token');
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            await axios.delete(`${API_URL}/cash-loan-payments/${paymentId}`);

            Swal.fire('Deleted!', 'Payment has been deleted.', 'success');
            fetchLoanDetails(); // Refresh payment history
        } catch (error) {
            console.error('Delete failed:', error);
            Swal.fire('Error', 'Failed to delete payment.', 'error');
        }
    };

    // 📝 Update payment
    const handleEditPayment = async (payment) => {
        const { value: formValues } = await Swal.fire({
            title: 'Update Payment',
            html: `
                <input id="swal-amount" type="number" min="1" step="0.01" value="${payment.amount}" class="swal2-input" placeholder="Amount" />
                <input id="swal-date" type="date" value="${payment.payment_date}" class="swal2-input" placeholder="Transaction Date" />
                <input id="swal-notes" type="text" value="${payment.notes || ''}" class="swal2-input" placeholder="Remark (optional)" />
            `,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Update',
            preConfirm: () => {
                const amount = document.getElementById('swal-amount').value;
                const date = document.getElementById('swal-date').value;
                const notes = document.getElementById('swal-notes').value;

                if (!amount || amount <= 0) {
                    Swal.showValidationMessage('Please enter a valid amount');
                    return false;
                }
                if (!date) {
                    Swal.showValidationMessage('Please select a transaction date');
                    return false;
                }
                return { amount, date, notes };
            }
        });

        if (!formValues) return;

        try {
            const token = localStorage.getItem('token');
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

            await axios.put(`${API_URL}/cash-loan-payments/${payment.id}`, {
                amount: formValues.amount,
                payment_date: formValues.date,
                notes: formValues.notes.trim(),
            });

            Swal.fire('Updated!', 'Payment has been updated successfully.', 'success');
            fetchLoanDetails();
        } catch (error) {
            console.error('Update failed:', error);
            Swal.fire('Error', 'Failed to update payment.', 'error');
        }
    };

    const handlePrint = () => {
        window.print();
    };

    if (loading)
        return (
            <div className="text-center mt-5">
                <div className="spinner-border text-primary" role="status" />
                <div>Loading...</div>
            </div>
        );

    if (!loan)
        return (
            <div className="alert alert-warning text-center mt-5">
                No loan found with ID: {id}
            </div>
        );

    return (
        <div className="container my-5" style={{ maxWidth: '1000px' }}>
            <div className="row g-4">
                {/* Left Panel - Loan Details */}
                <div className="col-lg-4 col-md-5 col-12">
                    <div className="card shadow-sm h-100">
                        <div className="card-body">
                            <h6 className="mb-4 text-primary fw-bold">Loan Details</h6>
                            <div className="mb-3">
                                <strong>Member:</strong>
                                <div className="text-muted">
                                    {loan.member?.firstname} {loan.member?.lastname}
                                </div>
                            </div>
                            <div className="mb-3">
                                <strong>Loan Amount:</strong>
                                <div className="text-success fw-semibold fs-5">
                                    ₹{loan.loan_amount_with_interest.toLocaleString()}
                                </div>
                            </div>
                            <div className="mb-3">
                                <strong>Balance:</strong>
                                <div className="text-danger fw-semibold fs-5">
                                    ₹{loan.balance.toLocaleString()}
                                </div>
                            </div>
                            <div className="mb-3">
                                <strong>Start Date:</strong>
                                <div>{loan.open_date}</div>
                            </div>
                            <div className="mb-3">
                                <strong>Tenure (weeks):</strong>
                                <div>{loan.tenure}</div>
                            </div>
                            <div className="mb-3">
                                <strong>Collection Type:</strong>
                                <div>{loan.collection_type}</div>
                            </div>
                            <div className="mb-3">
                                <strong>Calculated EMI:</strong>
                                <div className="fs-5 fw-bold">₹{calculatedEmi}</div>
                            </div>
                            <button
                                onClick={handlePrint}
                                className="btn btn-outline-primary w-100 mt-4 d-flex justify-content-center align-items-center gap-2"
                            >
                                <Icon icon="material-symbols:print" className="fs-5" />
                                Print Statement
                            </button>
                        </div>
                    </div>
                </div>

                {/* Right Panel - Payment & History */}
                <div className="col-lg-8 col-md-7 col-12">
                    <div className="card mb-4 shadow-sm">
                        <div className="card-body">
                            <h6 className="mb-4 text-primary fw-bold mb-10">Make a Payment</h6>
                            <hr />
                            <form onSubmit={handlePayEmi} className="row g-3 mt-10">
                                <div className="col-md-6">
                                    <label htmlFor="emiAmount" className="form-label fw-semibold">
                                        EMI Amount <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        id="emiAmount"
                                        type="number"
                                        className="form-control form-control-lg"
                                        placeholder={`Suggested: ₹${calculatedEmi}`}
                                        value={emiAmount}
                                        onChange={(e) => setEmiAmount(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label htmlFor="transactionDate" className="form-label fw-semibold">
                                        Transaction Date <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        id="transactionDate"
                                        type="date"
                                        className="form-control form-control-lg"
                                        value={transactionDate}
                                        onChange={(e) => setTransactionDate(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="col-12">
                                    <label htmlFor="remark" className="form-label fw-semibold">
                                        Remark
                                    </label>
                                    <input
                                        id="remark"
                                        type="text"
                                        className="form-control form-control-lg"
                                        placeholder="Optional note"
                                        value={remark}
                                        onChange={(e) => setRemark(e.target.value)}
                                    />
                                </div>

                                <div className="col-12 d-flex justify-content-end">
                                    <button
                                        type="submit"
                                        className="btn btn-primary-600 radius-8 px-14 py-6 text-sm"
                                    >
                                        Pay EMI
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* Payment History */}
                    <div className="card shadow-sm">
                        <div className="card-body">
                            <h6 className="mb-4 text-primary fw-bold">Payment History</h6>
                            {payments.length > 0 ? (
                                <div
                                    className="table-responsive"
                                    style={{ maxHeight: '350px', overflowY: 'auto' }}
                                >
                                    <table className="table table-hover table-striped text-center align-middle">
                                        <thead className="table-light sticky-top">
                                            <tr>
                                                <th>#</th>
                                                <th>Amount (₹)</th>
                                                <th>Transaction Date</th>
                                                <th>Remark</th>
                                                <th>Action</th>

                                            </tr>
                                        </thead>
                                        <tbody>
                                            {payments.map((payment, index) => (
                                                <tr key={payment.id}>
                                                    <td>{index + 1}</td>
                                                    <td className="text-success fw-semibold">
                                                        ₹{payment.amount.toLocaleString()}
                                                    </td>
                                                    <td>{payment.payment_date}</td>
                                                    <td>{payment.notes || '-'}</td>
                                                    <td>
                                                        <Icon
                                                            icon="material-symbols:edit-outline"
                                                            className="text-primary fs-5 cursor-pointer"
                                                            title="Edit Payment"
                                                            onClick={() => handleEditPayment(payment)}
                                                        /> |
                                                        <Icon
                                                            icon="material-symbols:delete-outline"
                                                            className="text-danger fs-5 cursor-pointer"
                                                            title="Delete Payment"
                                                            onClick={() => handleDeletePayment(payment.id)}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <p className="text-center fst-italic text-muted">
                                    No payments recorded yet.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Print-only content */}
            <div className="d-none d-print-block mt-5">
                <h3 className="mb-3">Loan Statement</h3>
                <div><strong>Member:</strong> {loan.member?.firstname} {loan.member?.lastname}</div>
                <div><strong>Loan Amount:</strong> ₹{loan.loan_amount.toLocaleString()}</div>
                <div><strong>Balance:</strong> ₹{loan.balance.toLocaleString()}</div>
                <div><strong>Start Date:</strong> {loan.open_date}</div>
                <div><strong>Tenure (weeks):</strong> {loan.tenure}</div>

                <h4 className="mt-4 mb-3">Payment History</h4>
                <table className="table table-bordered">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Amount (₹)</th>
                            <th>Transaction Date</th>
                            <th>Remark</th>

                        </tr>
                    </thead>
                    <tbody>
                        {payments.map((p, idx) => (
                            <tr key={p.id}>
                                <td>{idx + 1}</td>
                                <td>{p.amount.toLocaleString()}</td>
                                <td>{p.payment_date}</td>
                                <td>{p.remark || '-'}</td>

                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .d-print-block, .d-print-block * {
            visibility: visible;
          }
          .d-print-block {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
          }
        }
      `}</style>
        </div>
    );
};

export default CashLoanPaymentLayer;
